import create from 'zustand';
import {persist, createJSONStorage} from 'zustand/middleware';
import {
  setLocalStorage,
  getLocalStorage,
  removeLocalStorage,
} from '@/Utils/CommonUtils';
import {registerJourneyClear} from '@/Utils/userGroup';

export interface JourneyCardDeeplink {
  screen: string;
  params?: Record<string, any>;
}

export interface JourneyCard {
  id: string;
  surface: string;
  priority: number;
  title: string;
  body: string;
  cta: string;
  deeplink: JourneyCardDeeplink;
  expires_at: number;
  dismissible: boolean;
}

export interface JourneyEnvelope {
  v: number;
  server_time: number;
  ttl_s: number;
  resync_jitter_s: number;
  state: string;
  rung: number;
  re_entry: boolean;
  cards: JourneyCard[];
  effects: any[];
}

interface JourneyState {
  envelope: JourneyEnvelope | null;
  v: number;
  fetchedAt: number;
  // server_time - Date.now(), recomputed on every applied envelope so
  // expiry checks (journeyGates.ts) stay correct even on a skewed device clock.
  serverClockOffset: number;
  dismissedIds: string[];
  // Once-per-card-id impression guard, lifted out of JourneyCard's component
  // ref -- a ref resets on remount (tab switch, focus loss, etc.), which was
  // re-firing the impression ack + journey_card_shown for the same card.
  impressedIds: string[];
  // last attempted-sync timestamp per trigger (journey/v1/next_action call
  // sites) -- used by useJourneySync's per-trigger min-interval guards.
  lastSyncByTrigger: Record<string, number>;

  applyEnvelope: (envelope: JourneyEnvelope) => void;
  markDismissed: (id: string) => void;
  markImpressed: (id: string) => void;
  clearOnLogout: () => void;
}

// Backstop cap on the dismissed/impressed id lists so a long-lived session
// can't grow them unboundedly even if applyEnvelope's card-id intersection
// (below) somehow doesn't run for a stretch. Oldest ids drop off first.
const MAX_TRACKED_IDS = 50;

const addCappedId = (ids: string[], id: string): string[] => {
  if (ids.includes(id)) {
    return ids;
  }
  const next = [...ids, id];
  return next.length > MAX_TRACKED_IDS
    ? next.slice(next.length - MAX_TRACKED_IDS)
    : next;
};

const useJourneyStore = create(
  persist<JourneyState>(
    (set, get) => ({
      envelope: null,
      v: 0,
      fetchedAt: 0,
      serverClockOffset: 0,
      dismissedIds: [],
      impressedIds: [],
      lastSyncByTrigger: {},

      applyEnvelope: envelope => {
        const current = get();
        const incomingV = typeof envelope?.v === 'number' ? envelope.v : NaN;

        // Core transport invariant: a stale or replayed HTTP response must
        // never overwrite newer state. Mirrors the unchanged:true handling
        // in useJourneySync -- just record that a response landed.
        if (!Number.isFinite(incomingV) || incomingV <= current.v) {
          set({fetchedAt: Date.now()});
          return;
        }

        // Prune dismissed/impressed ids down to cards the new envelope still
        // references -- a card that has rotated out no longer needs tracking.
        const cardIds = new Set(
          (envelope?.cards || [])
            .map(card => card?.id)
            .filter((id): id is string => !!id),
        );

        set({
          envelope,
          v: incomingV,
          fetchedAt: Date.now(),
          serverClockOffset: (envelope?.server_time ?? Date.now()) - Date.now(),
          dismissedIds: current.dismissedIds.filter(id => cardIds.has(id)),
          impressedIds: current.impressedIds.filter(id => cardIds.has(id)),
        });
      },

      markDismissed: id => {
        const next = addCappedId(get().dismissedIds, id);
        if (next === get().dismissedIds) {
          return;
        }
        set({dismissedIds: next});
      },

      markImpressed: id => {
        const next = addCappedId(get().impressedIds, id);
        if (next === get().impressedIds) {
          return;
        }
        set({impressedIds: next});
      },

      // Registered into userGroup.ts's resetUserGroupToGuest (see
      // registerJourneyClear below) instead of being imported directly by
      // CommonUtils' handleLogout -- CommonUtils is where this store's own
      // storage functions (getLocalStorage/setLocalStorage/removeLocalStorage)
      // live, so a direct import back into CommonUtils would be a real
      // module cycle. resetUserGroupToGuest already runs unconditionally
      // inside handleLogout (the single logout path used by both explicit
      // sign-out and the 401 interceptor), so this still fires on every
      // logout without one.
      clearOnLogout: () =>
        set({
          envelope: null,
          v: 0,
          fetchedAt: 0,
          serverClockOffset: 0,
          dismissedIds: [],
          impressedIds: [],
          lastSyncByTrigger: {},
        }),
    }),
    {
      name: 'journey-state',
      version: 1,
      // createJSONStorage does its own JSON.stringify/parse around whatever
      // storage engine it's given -- getLocalStorage/setLocalStorage/
      // removeLocalStorage already round-trip a raw string through
      // localForage as-is, so they can be handed straight through here with
      // no extra manual JSON layer (that used to double-encode, which only
      // "worked" because encode/decode are symmetric; it also used the
      // deprecated `getStorage` option).
      storage: createJSONStorage(() => ({
        getItem: getLocalStorage,
        setItem: setLocalStorage,
        removeItem: removeLocalStorage,
      })),
    },
  ),
);

registerJourneyClear(() => {
  useJourneyStore.getState().clearOnLogout();
});

export {useJourneyStore};
